import React from 'react';
import { Link } from 'react-router-dom';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Star, CheckCircle, ArrowRight } from 'lucide-react';

const ReviewsPage = () => {
  const reviews = [
    {
      id: 1,
      name: 'Netbet',
      slug: 'netbet',
      rating: 4.5,
      bonus: 'Bonus de bienvenue de 100€',
      description: 'Un opérateur solide avec des cotes compétitives et une interface intuitive. Idéal pour les parieurs réguliers.',
      features: ['Cotes compétitives', 'Cash-out disponible', 'Application mobile optimisée'],
      color: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-400/50',
    },
    {
      id: 2,
      name: 'Unibet',
      slug: 'unibet',
      rating: 4.7,
      bonus: '10€ de dépôt gratuit + 100€ de bonus',
      description: 'L\'un des leaders du marché français avec une offre complète et des fonctionnalités innovantes.',
      features: ['10€ offerts sans dépôt', 'Bonus jusqu\'à 100€', 'Streaming en direct'],
      color: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-400/50',
    },
    {
      id: 3,
      name: 'Vbet',
      slug: 'vbet',
      rating: 4.3,
      bonus: 'Bonus de bienvenue de 100€',
      description: 'Un opérateur reconnu offrant une expérience de paris complète avec un excellent support client.',
      features: ['Pari en direct', 'Multiples sports', 'Support réactif'],
      color: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-400/50',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Disclaimer />
      <Header />
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            data-discover="true"
            className="inline-flex items-center gap-2 text-toxic-green hover:text-toxic-cyan mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 glass rounded-full border border-toxic-yellow/30">
              <Star className="w-5 h-5 text-toxic-yellow" />
              <span className="text-sm text-toxic-yellow font-semibold">Avis</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-toxic-yellow to-toxic-orange bg-clip-text text-transparent">
                Avis des meilleurs sites de paris
              </span>
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Découvrez nos analyses approfondies des opérateurs agréés en France pour faire le meilleur choix
            </p>
          </div>

          {/* Reviews grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {reviews.map((review) => (
              <Link
                key={review.id}
                to={`/reviews/${review.slug}`}
                data-discover="true"
                className="glass-strong rounded-xl overflow-hidden border-2 border-white/10 hover:border-toxic-yellow/50 transition-all hover:scale-105 group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${review.color} opacity-50 -z-10`}></div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-bold text-white group-hover:text-toxic-yellow transition-colors">
                      {review.name}
                    </h3>
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 text-toxic-yellow fill-toxic-yellow" />
                      <span className="text-white font-semibold">{review.rating}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-toxic-yellow font-semibold mb-2 text-sm">
                      {review.bonus}
                    </p>
                    <p className="text-white/80 text-sm leading-relaxed mb-3">
                      {review.description}
                    </p>
                  </div>

                  <ul className="mb-6 space-y-2">
                    {review.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-white/70">
                        <CheckCircle className="w-4 h-4 text-toxic-yellow flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="inline-flex items-center gap-2 text-toxic-yellow hover:text-toxic-orange font-semibold text-sm transition-colors group">
                    Lire l'avis complet
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* About our reviews section */}
          <div className="glass-strong rounded-2xl p-8 md:p-12 border border-toxic-yellow/30">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Comment nous testons les opérateurs
            </h2>
            
            <div className="space-y-6 text-white/80 leading-relaxed">
              <p>
                Nos avis sont le fruit d'un processus de test rigoureux et approfondi qui examine tous les aspects importants d'un opérateur de paris. 
                Nous ne nous contentons pas de lire les descriptions ou de consulter les sites web. Nous créons réellement des comptes, effectuons des 
                dépôts, plaçons des paris, testons les fonctionnalités, et évaluons chaque élément de l'expérience utilisateur.
              </p>
              
              <p>
                Chaque opérateur est testé sur plusieurs critères essentiels. La qualité des cotes est évaluée en comparant systématiquement les offres 
                sur une variété d'événements et de marchés. Nous testons les bonus et promotions pour comprendre leur valeur réelle et leurs conditions 
                de mise. L'interface utilisateur est évaluée pour sa facilité d'utilisation, sa rapidité, et son intuitivité.
              </p>
              
              <p>
                Les méthodes de paiement sont testées pour leur rapidité, leurs limites, et leurs frais. Le service client est évalué en posant des questions 
                réelles et en mesurant la rapidité et la qualité des réponses. Les applications mobiles sont testées sur différents appareils pour évaluer leur 
                performance et leur stabilité.
              </p>
              
              <p>
                Nous testons également les fonctionnalités avancées comme le cash-out, les paris en direct, le streaming vidéo, et les outils d'analyse. Ces 
                fonctionnalités peuvent faire la différence entre une expérience moyenne et une expérience exceptionnelle, et nous les évaluons en conséquence.
              </p>
              
              <p>
                Nos avis sont objectifs et indépendants. Nous ne favorisons aucun opérateur et présentons les forces et faiblesses de chacun de manière équilibrée. 
                Si un opérateur excelle dans certains domaines mais présente des limitations dans d'autres, nous le signalons clairement. Cette transparence vous 
                permet de faire des choix éclairés basés sur vos propres besoins et préférences.
              </p>
              
              <p>
                Nous mettons régulièrement à jour nos avis pour refléter les évolutions du marché. Les opérateurs améliorent constamment leurs offres, ajoutent de 
                nouvelles fonctionnalités, et ajustent leurs stratégies. Nos avis sont donc régulièrement révisés pour garantir qu'ils reflètent l'état actuel de 
                chaque opérateur.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReviewsPage;

